import { JobListItem } from '@commenda/api/job/types';
import {
  Badge,
  Button,
  DataTable,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icons,
} from '@commenda/kakashi';
import FileDownloader from '../../../components/file-downloader-wrapper';
import { formatDateTime } from '../../settings-tab/dataIntegration/common/utils';

interface JobsListTableProps {
  isOpen: boolean;
  onClose: () => void;
  data: JobListItem[];
  title?: string;
}

export const JobsListTable = ({
  isOpen,
  onClose,
  data,
  title = 'Job Reports',
}: JobsListTableProps) => {
  const getStatusBadge = (job: JobListItem) => {
    if (job.isJobCompleted) {
      if (job.failureCount && job.failureCount > 0) {
        return <Badge variant="error">Failed</Badge>;
      }
      return <Badge variant="success">Completed</Badge>;
    }
    return <Badge variant="blue">Processing</Badge>;
  };

  const columnGroups = [
    {
      id: 'id',
      columns: [
        {
          accessorKey: 'id',
          header: 'Job ID',
          title: 'Job ID',
          width: 250,
          cell: ({ row }) => (
            <p className="txt-small-regular text-text-primary truncate">
              {row.getValue('id')}
            </p>
          ),
        },
      ],
    },
    {
      id: 'status',
      columns: [
        {
          accessorKey: 'isJobCompleted',
          header: 'Status',
          title: 'Status',
          cell: ({ row }) => {
            const job = row.original as JobListItem;
            return getStatusBadge(job);
          },
        },
      ],
    },
    {
      id: 'started_at',
      columns: [
        {
          accessorKey: 'startedAt',
          header: 'Started At',
          title: 'Started At',
          showInSelector: false,
          cell: ({ row }) => {
            const job = row.original as JobListItem;
            const date = job.startedAt;
            const formattedDate = date ? formatDateTime(new Date(date)) : '-';
            return (
              <p className="txt-small-regular text-text-primary truncate">
                {formattedDate}
              </p>
            );
          },
        },
      ],
    },
    {
      id: 'finished_at',
      columns: [
        {
          accessorKey: 'finishedAt',
          header: 'Finished At',
          title: 'Finished At',
          showInSelector: false,
          cell: ({ row }) => {
            const job = row.original as JobListItem;
            const date = job.finishedAt;
            const formattedDate = date ? formatDateTime(new Date(date)) : '-';
            return (
              <p className="txt-small-regular text-text-primary truncate">
                {formattedDate}
              </p>
            );
          },
        },
      ],
    },
    {
      id: 'file',
      columns: [
        {
          accessorFn: (row) => row.file?.fileName || '',
          accessorKey: 'file',
          showInSelector: false,
          header: 'File Name',
          title: 'File Name',
          width: 250,
          cell: ({ row }) => {
            const job = row.original as JobListItem;
            return (
              <p className="txt-small-regular text-text-primary truncate">
                {job.file?.fileName || '-'}
              </p>
            );
          },
        },
      ],
    },
    {
      id: 'total_rows',
      columns: [
        {
          accessorKey: 'totalRows',
          header: 'Total Rows',
          title: 'Total Rows',
          cell: ({ row }) => {
            const job = row.original as JobListItem;
            return (
              <div className="flex items-center gap-2">
                <p className="txt-small-regular text-text-primary">
                  {job.totalRows || '-'}
                </p>

                {job.successCount !== null &&
                  job.successCount !== undefined &&
                  job.successCount > 0 && (
                    <Badge variant="success">Success: {job.successCount}</Badge>
                  )}
                {job.failureCount !== null &&
                  job.failureCount !== undefined &&
                  job.failureCount > 0 && (
                    <Badge variant="error">Failed: {job.failureCount}</Badge>
                  )}
              </div>
            );
          },
        },
      ],
    },
    {
      id: 'processed_rows',
      columns: [
        {
          accessorKey: 'processedRows',
          header: 'Processed Rows',
          title: 'Processed Rows',
          showInSelector: false,
          cell: ({ row }) => {
            const job = row.original as JobListItem;
            return (
              <p className="txt-small-regular text-text-primary">
                {job.processedRows || '-'}
              </p>
            );
          },
        },
      ],
    },
    {
      id: 'actions',
      sticky: 'right' as const,
      columns: [
        {
          id: 'actions',
          accessorKey: 'actions',
          header: '',
          showInSelector: false,
          cell: ({ row }) => {
            const job = row.original as JobListItem;
            const hasFile = job.file?.id;

            return (
              <FileDownloader file={job.file} token={undefined}>
                <Button variant="secondary" size="sm" disabled={!hasFile}>
                  <Icons.Download01 className="size-4" />
                  <span>Download Report</span>
                </Button>
              </FileDownloader>
            );
          },
        },
      ],
    },
  ];

  return (
    <Dialog open={isOpen}>
      <DialogContent
        className="bg-white min-w-[700px] max-w-[80vw] flex flex-col max-h-[70vh] p-0 overflow-hidden"
        hideCloseIcon
        preventCloseOnOutsideClick
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="p-6 overflow-y-auto flex-1">
          <DataTable
            columnGroups={columnGroups}
            data={data}
            tableViewConfig={{
              isEnabled: true,
              defaultView: 'regular',
            }}
            rowClassName={() => 'cursor-default hover:bg-primary'}
            columnSelectorConfig={{
              isEnabled: true,
              placeholder: 'Customise columns',
              searchText: 'Search columns',
              notFoundText: 'No columns found',
              selectAllText: 'Show all columns',
              defaultVisibleColumns: [
                'id',
                'status',
                'startedAt',
                'finishedAt',
                'file',
                'totalRows',
                'processedRows',
                'actions',
              ],
            }}
            searchConfig={{
              isEnabled: true,
              placeholder: 'Search by file name or ID',
              columnsToEnableSearchFor: ['file', 'id'],
            }}
            emptyStateConfig={{
              title: 'No jobs found',
              description: 'There are no jobs to display at this time.',
            }}
            paginationConfig={{
              isEnabled: true,
              pageSize: 5,
              type: 'PAGE',
            }}
          />
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} size="sm" type="button">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
